"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LEGACY_FINANCIAL_YEAR_CUTOFF_ISO = void 0;
exports.getFinancialYearKey = getFinancialYearKey;
exports.getFinancialYearDbFilter = getFinancialYearDbFilter;
exports.isLegacyFinancialYear = isLegacyFinancialYear;
exports.getEffectiveFinancialYearDbFilter = getEffectiveFinancialYearDbFilter;
exports.recentFinancialYearKeys = recentFinancialYearKeys;
function startYearContainingInstant(date) {
    const year = date.getFullYear();
    const monthIndex = date.getMonth();
    return monthIndex >= 3 ? year : year - 1;
}
function dbFilterFromStartYear(startYear) {
    const endYear = startYear + 1;
    const start = new Date(startYear, 3, 1, 0, 0, 0, 0);
    const end = new Date(endYear, 2, 31, 23, 59, 59, 999);
    return {
        startIso: start.toISOString(),
        endIso: end.toISOString(),
    };
}
/**
 * Compact FY key for the India FY that contains **date**, e.g. **`"2024-25"`**.
 */
function getFinancialYearKey(date) {
    const startYear = startYearContainingInstant(date);
    const endYear = startYear + 1;
    return `${startYear}-${String(endYear % 100).padStart(2, '0')}`;
}
/**
 * ISO bounds for **`fyKey`** (`"YYYY-YY"`), or **`null`** if the key is invalid.
 */
function getFinancialYearDbFilter(fyKey) {
    const m = fyKey.trim().match(/^(\d{4})-(\d{2})$/);
    if (!m)
        return null;
    const startYear = Number(m[1]);
    const endSuffix = Number(m[2]);
    const endYear = startYear + 1;
    if (endSuffix !== endYear % 100)
        return null;
    return dbFilterFromStartYear(startYear);
}
/**
 * Instant at the start of India FY that begins 1 April 2026 (`2026-27`).
 * FYs whose `endIso` is on or before this instant are considered **legacy** for
 * payment rollups and related logic (e.g. transaction `meta.version` rules).
 */
exports.LEGACY_FINANCIAL_YEAR_CUTOFF_ISO = '2026-04-01T00:00:00.000Z';
/**
 * Whether **`financialYearKey`** denotes an India FY that ends on/before
 * {@link LEGACY_FINANCIAL_YEAR_CUTOFF_ISO} (e.g. `2025-26` and earlier).
 * Invalid keys return **`false`**.
 */
function isLegacyFinancialYear(financialYearKey) {
    const fy = getFinancialYearDbFilter(financialYearKey);
    if (!fy)
        return false;
    return fy.endIso < exports.LEGACY_FINANCIAL_YEAR_CUTOFF_ISO;
}
/**
 * Returns FY bounds with payment mode.
 *
 * - `null` if FY key is invalid
 * - Legacy FY → startIso set to cutoff, mode = 'legacy'
 * - Standard FY → unchanged bounds, mode = 'standard'
 */
function getEffectiveFinancialYearDbFilter(financialYearKey) {
    const bounds = getFinancialYearDbFilter(financialYearKey);
    if (!bounds)
        return null;
    const isLegacy = isLegacyFinancialYear(financialYearKey);
    return isLegacy
        ? Object.assign(Object.assign({}, bounds), { startIso: new Date(0).toISOString(), endIso: bounds.endIso, mode: 'legacy' }) : Object.assign(Object.assign({}, bounds), { mode: 'standard' });
}
/**
 * Newest first: current FY and **`extraPast`** prior FYs (e.g. **`2`** → 3 options).
 */
function recentFinancialYearKeys(anchor, extraPast) {
    const sy = startYearContainingInstant(anchor);
    return Array.from({ length: extraPast + 1 }, (_, i) => {
        const y = sy - i;
        const ey = y + 1;
        return `${y}-${String(ey % 100).padStart(2, '0')}`;
    });
}
//# sourceMappingURL=financialYear.js.map